import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';

import { App, isMockModeEnv } from './app.js';
import { ConfigProvider } from './tenant-config-module/config-provider.js';
import { BootstrapSkeleton } from './ui-module/bootstrap-skeleton.js';
import { createI18nInstance } from './i18n-module/i18n.js';
import { readInitialLocale } from './i18n-module/use-language.js';
import { buildMockDemoToken } from './dev-module/mock-demo-token.js';
import './index.css';

const CANCEL_PATH_PREFIX = '/cancel/';

/**
 * Bootstrap-Sequenz:
 *
 * 1. i18next mit der initialen Locale initialisieren (persistierte Wahl >
 *    Browser-Sprache > Default DE).
 * 2. Im Mock-Mode: falls die URL noch keine `/cancel/<token>`-Route trägt,
 *    einen Demo-Token erzeugen und per `history.replaceState` aktivieren.
 * 3. React mit `<I18nextProvider>` + `<ConfigProvider>` + `<BrowserRouter>`
 *    mounten — der ConfigProvider rendert den Skeleton, lädt die Tenant-
 *    Config, setzt CSS-Variablen und übergibt dann an `<App>`.
 *
 * Bootstrap-Fehler werden vom Provider in den Bootstrap-State geschrieben
 * und in `<App>` als `<BootstrapError>` gerendert — keine FOUC.
 */
async function bootstrap(): Promise<void> {
  const rootElement = document.getElementById('root');
  if (rootElement === null) {
    throw new Error('Root-Element #root fehlt im HTML.');
  }
  const initialLocale = readInitialLocale();
  const i18n = await createI18nInstance(initialLocale);
  document.documentElement.lang = initialLocale;

  ensureMockDemoRoute();
  createRoot(rootElement).render(
    <StrictMode>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter>
          <ConfigProvider skeleton={<BootstrapSkeleton />} isMockMode={isMockModeEnv()}>
            <App />
          </ConfigProvider>
        </BrowserRouter>
      </I18nextProvider>
    </StrictMode>,
  );
}

/**
 * Im Mock-Mode: setzt einen Demo-Token-Pfad, falls die App ohne Route gestartet
 * wurde (z. B. lokaler `vite`-Aufruf ohne Deep-Link). Der Pin-Fingerprint
 * ist seit dem Wire-Format `0x01` Teil des Tokens; ein URL-Fragment wird nicht
 * mehr benötigt.
 */
function ensureMockDemoRoute(): void {
  if (!isMockModeEnv()) {
    return;
  }
  if (window.location.pathname.startsWith(CANCEL_PATH_PREFIX)) {
    return;
  }
  const token = buildMockDemoToken();
  const newUrl = `${CANCEL_PATH_PREFIX}${token}${window.location.search}`;
  window.history.replaceState(null, '', newUrl);
}

void bootstrap();
